import React from 'react';

import { useAuthentication } from '@context/AuthContext';
import { useCurrentUser } from '@context/UsersContext';
import { listenToChats } from '@db/Chats';
import { createNamedContext, useRequiredContext } from '@helpers/Context';
import { sortBy } from '@helpers/Formatters';
import { logError } from '@helpers/Loggers';
import { getCurrentUserId } from '@helpers/firebase/Auth';
import { Chat } from '@types';

export type ChatsContextProps = {
  chats: Chat[];
  unreadMessageCount: number;
};

export const ChatsContext = createNamedContext<ChatsContextProps>({
  ChatsContext: undefined,
});

export function useChats() {
  return useRequiredContext(ChatsContext);
}

export function ChatsProvider({ children }: React.PropsWithChildren) {
  const { loggedIn } = useAuthentication();
  const { blockedUserIds } = useCurrentUser();
  const [chats, setChats] = React.useState<Chat[]>([]);
  const currentUserId = loggedIn ? getCurrentUserId() : undefined;
  const unreadMessageCount = React.useMemo(
    () =>
      currentUserId
        ? chats.reduce((count, chat) => count + (chat.unreadMessageCount[currentUserId] || 0), 0)
        : 0,
    [chats, currentUserId]
  );

  React.useEffect(() => {
    if (loggedIn)
      // TODO: limit the number of chats loaded at once,
      // need to be careful about the unread message count
      return listenToChats(
        (chats) =>
          setChats(
            sortBy(
              chats.filter((chat) => !blockedUserIds.has(chat.createdBy)),
              'updatedAt',
              'desc'
            )
          ),
        (error) => logError(error)
      );
  }, [blockedUserIds, loggedIn]);

  const value = { chats, unreadMessageCount };

  return <ChatsContext.Provider value={value}>{children}</ChatsContext.Provider>;
}
